import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listSlackChannels } from "../services/notifications/slackApi";
import {
  deleteDeliveryRule,
  listDeliveryRules,
  setDeliveryRuleStatus,
} from "../services/notifications/deliveryRulesApi";
import { mapRulesToDeliveryRows } from "../services/notifications/deliveryRulesMappers";
import { deleteDistribution, listDistributions, setDistributionStatus } from "../services/distributions/distributionsApi";
import { waitForDistributionEdit } from "../services/distributions/distributionEdits";
import type { WriteAcceptedApi } from "../services/distributions/distributions.types";
import { toDeliveryRows } from "../services/delivery/deliverStoryDistribution";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { Delivery } from "../components/organisms/DeliverStory/types";
import type { DeliveryRuleDto } from "../services/notifications/deliveryRules.types";

/** Backend caps this at 500 (`rules_routes.py`) and returns a bare array
 * with no total count, so the table paginates the full set client-side —
 * see M5 in DELIVERY_FLOW_CRUD_PLAN.md. */
const LIST_LIMIT = 500;

export interface UseDeliveryRules {
  /** Table-ready rows — already mapped through the DTO ⇄ UI mappers. */
  deliveries: Delivery[];
  /** Raw DTOs, kept for callers that need the full wire shape (e.g. the
   * edit flow reading `trigger_config`/`content_config` back out). */
  rules: DeliveryRuleDto[];
  isLoading: boolean;
  isError: boolean;
  /** Backend's actual error string, or null when there's no error —
   * always render this, never a hardcoded fallback (CLAUDE.md). */
  error: string | null;
  /** PATCH { status: "paused" } — only legal from `active` (row menu already
   * hides this action otherwise); a 409 surfaces via `actionError`. */
  pause: (ruleId: string) => void;
  /** PATCH { status: "active" } — only legal from `paused`, and only for a
   * `scheduled` rule server-side (§1.4 transition table). */
  resume: (ruleId: string) => void;
  /** PATCH { status: "cancelled" } — terminal; row keeps only Delete after. */
  cancel: (ruleId: string) => void;
  /** DELETE /rules/{id} — permanent, story-owner only server-side. */
  remove: (ruleId: string) => void;
  /** True while any of pause/resume/cancel/remove is in flight — callers
   * disable the row's action menu so a second click can't double-fire. */
  isActionPending: boolean;
  /** Backend's actual error string from the most recent pause/resume/
   * cancel/delete failure (e.g. the 409 transition message or the 403
   * "story owner only" delete message) — null once cleared or on success. */
  actionError: string | null;
  clearActionError: () => void;
}

/**
 * Lists a story's delivery rules and maps them to table rows.
 *
 * Fetches the Slack channel list alongside the rules so `target_ref`
 * (an opaque Slack channel id) can be resolved to a human-readable
 * `#channel-name` (M3) — the same cached query `SendNowForm`'s channel
 * picker already populates, so this is a cache hit, not an extra
 * network call, once that picker has been opened once.
 */
export const useDeliveryRules = (
  storyId?: string,
  enabled = true,
  /** The tenant runs the new architecture: the rows are the Story's "Deliver Story" distributions. `null` while unknown. */
  newArchitecture: boolean | null = false,
): UseDeliveryRules => {
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const onRules = newArchitecture === false;
  const onDistributions = newArchitecture === true;

  const channelsQuery = useQuery({
    queryKey: queryKeys.notifications.slackChannels(),
    queryFn: listSlackChannels,
    enabled: enabled && onRules,
  });

  const channelNameById = useMemo(
    () => new Map<string, string>((channelsQuery.data?.channels ?? []).map((c) => [c.id, `#${c.name}`])),
    [channelsQuery.data],
  );

  const rulesQuery = useQuery({
    queryKey: queryKeys.notifications.deliveryRules(storyId ?? ""),
    queryFn: () => listDeliveryRules({ story_uuid: storyId, limit: LIST_LIMIT }),
    enabled: enabled && onRules && Boolean(storyId),
  });

  const distributionsQuery = useQuery({
    queryKey: queryKeys.distributions.list(storyId ?? ""),
    queryFn: () => listDistributions(storyId ?? ""),
    enabled: enabled && onDistributions && Boolean(storyId),
  });

  const deliveries = useMemo(
    () =>
      onDistributions
        ? toDeliveryRows(distributionsQuery.data?.items ?? [])
        : mapRulesToDeliveryRows(rulesQuery.data ?? [], channelNameById),
    [onDistributions, distributionsQuery.data, rulesQuery.data, channelNameById],
  );
  const listQuery = onDistributions ? distributionsQuery : rulesQuery;

  /** A distribution's change is real once Story Manager has applied it. */
  const applied = async (write: Promise<WriteAcceptedApi>): Promise<void> => {
    await waitForDistributionEdit(storyId ?? "", (await write).request_id);
  };
  const setStatus = (id: string, status: "active" | "paused" | "cancelled"): Promise<unknown> => {
    if (!onDistributions) return setDeliveryRuleStatus(id, status);
    if (status === "cancelled") return applied(deleteDistribution(storyId ?? "", id));
    return applied(setDistributionStatus(storyId ?? "", id, { status }));
  };
  const removeOne = (id: string): Promise<unknown> =>
    onDistributions ? applied(deleteDistribution(storyId ?? "", id)) : deleteDeliveryRule(id);

  // Shared by all four row actions — a successful pause/resume/cancel/delete
  // always changes what the list should show, so refetch it rather than
  // hand-patching the cached array (the backend also recomputes
  // `next_run_at` server-side on resume, which the client can't derive).
  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: onDistributions
        ? queryKeys.distributions.list(storyId ?? "")
        : queryKeys.notifications.deliveryRules(storyId ?? ""),
    });

  const pauseMutation = useMutation({
    mutationFn: (ruleId: string) => setStatus(ruleId, "paused"),
    onSuccess: () => {
      setActionError(null);
      invalidateList();
    },
    onError: (err: unknown) => setActionError(getApiErrorMessage(err, "Couldn't pause this delivery.")),
  });

  const resumeMutation = useMutation({
    mutationFn: (ruleId: string) => setStatus(ruleId, "active"),
    onSuccess: () => {
      setActionError(null);
      invalidateList();
    },
    onError: (err: unknown) => setActionError(getApiErrorMessage(err, "Couldn't resume this delivery.")),
  });

  const cancelMutation = useMutation({
    mutationFn: (ruleId: string) => setStatus(ruleId, "cancelled"),
    onSuccess: () => {
      setActionError(null);
      invalidateList();
    },
    onError: (err: unknown) => setActionError(getApiErrorMessage(err, "Couldn't cancel this delivery.")),
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => removeOne(ruleId),
    onSuccess: () => {
      setActionError(null);
      invalidateList();
    },
    onError: (err: unknown) => setActionError(getApiErrorMessage(err, "Couldn't delete this delivery.")),
  });

  return {
    deliveries,
    rules: onRules ? rulesQuery.data ?? [] : [],
    isLoading: newArchitecture === null || listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.isError
      ? getApiErrorMessage(listQuery.error, "Couldn't load deliveries.")
      : null,
    pause: (ruleId: string) => pauseMutation.mutate(ruleId),
    resume: (ruleId: string) => resumeMutation.mutate(ruleId),
    cancel: (ruleId: string) => cancelMutation.mutate(ruleId),
    remove: (ruleId: string) => deleteMutation.mutate(ruleId),
    isActionPending:
      pauseMutation.isPending ||
      resumeMutation.isPending ||
      cancelMutation.isPending ||
      deleteMutation.isPending,
    actionError,
    clearActionError: () => setActionError(null),
  };
};
